/**
 * The task page's action column: the one filled primary action from the
 * server's available transitions, its quieter siblings, and the Owner's
 * edit, dependency and deletion actions, plus adding a reference.
 */

import type { ReactNode } from 'react';

import type { TaskView } from '../../api.js';
import { useWorkspaceCopy } from '../../components.js';
import { useSession } from '../../session.js';
import { Button } from '../../ui.js';
import type { DialogKind } from './useTaskDialogs.js';

export function TaskActionButtons({
  view,
  openDialog,
}: {
  readonly view: TaskView;
  readonly openDialog: (kind: Exclude<DialogKind, null>, view: TaskView) => void;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const task = view.task;
  const transitions = view.availableTransitions;
  const isBacklog = task.status === 'BACKLOG';

  /** One primary action per view; everything else renders quieter. */
  const primary: string | null = transitions.includes('claim')
    ? 'claim'
    : transitions.includes('submit')
      ? 'submit'
      : transitions.includes('approve')
        ? 'approve'
        : transitions.includes('complete')
          ? 'complete'
          : null;

  const actionLabel: Record<string, string> = {
    claim: copy.actions.claim,
    submit: copy.actions.submit,
    approve: copy.actions.approve,
    reject: copy.actions.reject,
    complete: copy.actions.complete,
    // Never the same words as a reviewer's approval.
    complete_own: copy.actions.completeOwn,
    return: copy.actions.returnToProgress,
  };

  const mayAdminister =
    session.status === 'authenticated' && session.profile.mayAdministerTasks;

  // The one filled action, plus quieter siblings.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        marginBottom: 'var(--space-lg)',
      }}
    >
      {primary !== null ? (
        <Button
          variant="primary"
          onClick={() => {
            openDialog(primary as Exclude<DialogKind, null>, view);
          }}
        >
          {actionLabel[primary]}
        </Button>
      ) : null}
      {transitions
        .filter((transition) => transition !== primary)
        .map((transition) => (
          <Button
            key={transition}
            variant={transition === 'reject' ? 'danger' : 'outline'}
            onClick={() => {
              openDialog(transition as Exclude<DialogKind, null>, view);
            }}
          >
            {actionLabel[transition] ?? transition}
          </Button>
        ))}
      {/* The Owner's edit, offered in every state — the whole point
                of it. Dependencies keep their own Backlog-era button
                because that dialog is a contributor-facing shortcut. */}
      {mayAdminister ? (
        <Button
          variant="outline"
          onClick={() => {
            openDialog('edit', view);
          }}
        >
          {copy.actions.edit}
        </Button>
      ) : null}
      {isBacklog &&
      session.status === 'authenticated' &&
      session.profile.mayAdministerTasks ? (
        <Button
          variant="outline"
          onClick={() => {
            openDialog('dependencies', view);
          }}
        >
          {copy.execution.editDependencies}
        </Button>
      ) : null}
      {session.status === 'authenticated' && session.profile.mayAdministerTasks ? (
        <Button
          variant="danger"
          onClick={() => {
            openDialog('delete', view);
          }}
        >
          {copy.deletion.action}
        </Button>
      ) : null}
      {(task.status === 'IN_PROGRESS' || task.status === 'IN_REVIEW') &&
      task.assignee !== null ? (
        <Button
          variant="quiet"
          onClick={() => {
            openDialog('reference', view);
          }}
        >
          {copy.task.addReference}
        </Button>
      ) : null}
    </div>
  );
}
